import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { ModelSelectorDialog } from './ModelSelectorDialog';
import { Button } from '../../ui/button';
import type { Model } from '../types';

// Re-export Model type for convenience
export type { Model };

interface ModelSelectorProps {
  selectedModel: string;
  onModelChange: (model: string) => void;
  models: Model[];
  disabled?: boolean;
}

// Map model IDs to short display names
const MODEL_SHORT_NAMES: Record<string, string> = {
  'claude-sonnet-4-5': 'Sonnet',
  'claude-opus-4-1': 'Opus',
  'gpt-5': 'GPT5',
  'gpt-5-mini': 'GPT5m',
  'gpt-5-nano': 'GPT5n',
  'claude-haiku-4-5': 'Haiku',
  'grok-code-fast-1': 'Grok',
  'grok-4-fast-reasoning': 'Grok4',
};

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  selectedModel,
  onModelChange,
  models,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dialogPosition, setDialogPosition] = useState({ top: 0, left: 0, width: 0 });
  const isClosingRef = useRef(false);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updatePosition = () => {
        const button = buttonRef.current;
        if (!button) return;

        // Try to find the message input container by looking for data attribute or class
        let inputContainer = button.closest('[data-message-input-container="true"]');

        // Fallback: look for the container with the textarea
        if (!inputContainer) {
          inputContainer = button.closest('.relative');
        }

        // Last fallback: look for parent with reasonable width
        if (!inputContainer) {
          let parent = button.parentElement;
          while (parent) {
            const rect = parent.getBoundingClientRect();
            // Found a parent with reasonable width (more than 300px)
            if (rect.width > 300) {
              inputContainer = parent;
              break;
            }
            parent = parent.parentElement;
          }
        }

        if (!inputContainer) return;

        const inputRect = inputContainer.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();

        setDialogPosition({
          top: buttonRect.top,
          left: inputRect.left,
          width: inputRect.width,
        });
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      };
    }
  }, [isOpen]);

  const handleModelSelect = (model: string) => {
    onModelChange(model);
    setIsOpen(false);
  };

  const handleButtonClick = () => {
    if (isOpen) {
      // Just close if already open
      setIsOpen(false);
    } else {
      // Open if closed
      setIsOpen(true);
    }
  };

  const handleDialogClose = () => {
    isClosingRef.current = true;
    setIsOpen(false);
    // Reset the flag after a short delay
    setTimeout(() => {
      isClosingRef.current = false;
    }, 100);
  };

  const shortName = MODEL_SHORT_NAMES[selectedModel] || 'Model';

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={handleButtonClick}
        disabled={disabled}
        variant="outline"
        size="sm"
        type="button"
        className="px-2 py-1 text-xs font-medium flex items-center gap-1"
        data-model-selector-button="true"
      >
        <span>{shortName}</span>
        <ChevronDown className="h-3 w-3 opacity-50" />
      </Button>

      {isOpen && (
        <ModelSelectorDialog
          models={models}
          selectedModel={selectedModel}
          onSelect={handleModelSelect}
          onClose={handleDialogClose}
          position={dialogPosition}
        />
      )}
    </>
  );
};
